import { LitElement, html, css, nothing, type PropertyValues } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import "../components/index.js";
import { toast, type BnBadgeVariant, type BnColumn, type BnRow } from "../components/index.js";
import { navigate } from "../nav.js";
import { detailedError, humanError } from "../api/errors.js";
import { getRepository, type RepositoryDetail } from "../api/repositories.js";
import { listTeams, roleAtLeast, type Role } from "../api/teams.js";
import {
  getDataArea,
  getMonth,
  deleteDataFile,
  deleteUpload,
  sealMonth,
  pullMonth,
  uploadFile,
  getDataSource,
  setDataSource,
  deleteDataSource,
  monthAllowed,
  monthOf,
  type MonthSummary,
  type MonthDetail,
  type Upload,
  type DataSource,
  type PullStatus,
} from "../api/data.js";

const POLL_MS = 2000;

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  return `${(n / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

function formatTime(iso: string): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return isNaN(d.getTime()) ? "—" : d.toLocaleString();
}

function plural(n: number, one: string, many: string): string {
  return `${n} ${n === 1 ? one : many}`;
}

function percent(sent: number, size: number): number {
  return size === 0 ? 100 : Math.floor((sent / size) * 100);
}

/** hidden reports a path the server refuses: a segment starting with ".". */
function hidden(path: string): boolean {
  return path.split("/").some((s) => s.startsWith("."));
}

/**
 * folderPath is a file's path inside the picked folder. The browser's
 * webkitRelativePath starts with the folder's own name, which is not part of
 * the data.
 */
function folderPath(f: File): string {
  const parts = (f.webkitRelativePath || f.name).split("/");
  return parts.length > 1 ? parts.slice(1).join("/") : f.name;
}

function pullVariant(s: PullStatus): BnBadgeVariant {
  return s === "succeeded" ? "success" : s === "failed" ? "error" : "info";
}

/** Transfer is one file this browser sends; it lives only as long as the page. */
interface Transfer {
  key: number;
  repositoryId: string;
  month: string;
  path: string;
  file: File;
  sent: number;
  state: "waiting" | "sending" | "done" | "failed";
  error: string;
}

/**
 * page-data (/repositories/:id/data) is a repository's data area: its months,
 * each with an open data drop that takes uploads and the seals that made
 * earlier drops read-only. Builds of the repository's workspaces read the
 * newest seal of every month. Uploads go in chunks and resume from what the
 * server already has, so a broken upload continues when the same file is
 * picked again. A maintainer can add an S3 source to pull a month from a
 * bucket instead.
 *
 * The routes behind the page exist only while the server has the data area
 * on; the first read tells that apart and the page then shows a notice.
 */
@customElement("page-data")
export class PageData extends LitElement {
  @property() repositoryId = "";

  @state() private repo: RepositoryDetail | null = null;
  @state() private teamRole: Role = "viewer";
  /** null while the data area is off on the server. */
  @state() private months: MonthSummary[] | null = null;
  @state() private loading = true;
  @state() private error = "";

  @state() private monthInput = monthOf(new Date());
  @state() private monthError = "";
  @state() private selected = "";
  @state() private detail: MonthDetail | null = null;
  @state() private detailLoading = false;
  @state() private detailError = "";

  @state() private transfers: Transfer[] = [];
  private resumeTarget: Upload | null = null;

  @state() private pendingFileDelete = "";
  @state() private pendingUploadDelete: Upload | null = null;
  @state() private sealConfirm = false;
  @state() private sealBusy = false;
  /** The seal being watched until it shows among the month's seals. */
  @state() private sealWatch: { month: string; sealId: string } | null = null;

  @state() private source: DataSource | null = null;
  @state() private sourceError = "";
  @state() private showSourceForm = false;
  @state() private srcEndpoint = "";
  @state() private srcRegion = "";
  @state() private srcBucket = "";
  @state() private srcPrefix = "";
  @state() private srcFormError = "";
  @state() private srcSaving = false;
  @state() private sourceDeleteConfirm = false;
  @state() private pullBusy = false;

  private nextKey = 0;
  private sending = false;
  private abort = new AbortController();
  private watchTimer?: number;
  private ticking = false;

  static override styles = css`
    :host {
      display: block;
      font-family: var(--bino-font-sans);
      color: var(--bino-text);
    }
    .back {
      display: inline-flex;
      align-items: center;
      gap: var(--bino-space-xs);
      font-size: var(--bino-font-size-md);
      color: var(--bino-text-secondary);
      text-decoration: none;
      margin-bottom: var(--bino-space-md);
    }
    .back:hover {
      color: var(--bino-text);
    }
    .head {
      display: flex;
      align-items: center;
      gap: var(--bino-space-md);
      margin-bottom: var(--bino-space-lg);
      flex-wrap: wrap;
    }
    h1 {
      margin: 0;
      font-size: var(--bino-font-size-lg);
      font-weight: 600;
      color: var(--bino-text);
    }
    h3 {
      margin: var(--bino-space-lg) 0 var(--bino-space-sm);
      padding: 0 var(--bino-space-lg);
      font-size: var(--bino-font-size-md);
      font-weight: 600;
    }
    .spacer {
      flex: 1;
    }
    .sections {
      display: flex;
      flex-direction: column;
      gap: var(--bino-space-lg);
    }
    .pad {
      padding: var(--bino-space-md) var(--bino-space-lg);
    }
    .meta {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(12rem, 1fr));
      gap: var(--bino-space-md);
    }
    .meta .k {
      font-size: var(--bino-font-size-sm);
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.05em;
      color: var(--bino-text-secondary);
      margin-bottom: var(--bino-space-xs);
    }
    .meta .v {
      font-family: var(--bino-font-mono);
      font-size: var(--bino-font-size-md);
      color: var(--bino-text);
      word-break: break-all;
    }
    .hint {
      font-size: var(--bino-font-size-sm);
      color: var(--bino-text-secondary);
      margin: 0;
    }
    .err {
      font-size: var(--bino-font-size-sm);
      color: var(--bino-error);
      margin: 0;
    }
    .row {
      display: flex;
      align-items: flex-end;
      gap: var(--bino-space-sm);
      flex-wrap: wrap;
    }
    .actions-row {
      display: flex;
      align-items: center;
      gap: var(--bino-space-sm);
      flex-wrap: wrap;
    }
    .mono {
      font-family: var(--bino-font-mono);
    }
    .link-btn {
      background: none;
      border: none;
      padding: 0;
      cursor: pointer;
      font-family: var(--bino-font-mono);
      font-size: var(--bino-font-size-base);
      color: var(--bino-primary);
      text-decoration: underline;
    }
    .link-btn:hover {
      color: var(--bino-primary-hover);
    }
    .progress {
      display: flex;
      align-items: center;
      gap: var(--bino-space-sm);
      min-width: 10rem;
    }
    progress {
      flex: 1;
      height: 0.5rem;
      accent-color: var(--bino-primary);
    }
    .warn {
      margin-top: var(--bino-space-md);
      padding: var(--bino-space-md);
      background: var(--bino-warning-bg);
      border: 1px solid var(--bino-warning-border);
      border-radius: var(--bino-radius);
      font-size: var(--bino-font-size-base);
      color: var(--bino-warning-text);
    }
  `;

  override connectedCallback() {
    super.connectedCallback();
    if (this.abort.signal.aborted) {
      this.abort = new AbortController();
      void this.send();
    }
    if (this.sealWatch || this.source?.lastPull?.status === "running") this.startWatch();
  }

  override disconnectedCallback() {
    super.disconnectedCallback();
    // Uploads stop where they are; the server keeps what arrived, so picking
    // the same files again later continues them.
    this.abort.abort();
    this.stopWatch();
  }

  override updated(changed: PropertyValues) {
    if (changed.has("repositoryId") && this.repositoryId) {
      if (changed.get("repositoryId")) {
        this.abort.abort();
        this.abort = new AbortController();
        this.transfers = [];
        this.selected = "";
        this.detail = null;
        this.sealWatch = null;
        this.stopWatch();
      }
      void this.load();
    }
  }

  private async load() {
    const id = this.repositoryId;
    const stale = () => !this.isConnected || id !== this.repositoryId;
    this.loading = true;
    this.error = "";
    try {
      const [repo, area, teams] = await Promise.all([
        getRepository(id),
        getDataArea(id),
        // Only hides controls the server would refuse anyway.
        listTeams().catch(() => []),
      ]);
      if (stale()) return;
      this.repo = repo;
      this.months = area ? sortMonths(area.months) : null;
      this.teamRole = teams.find((t) => t.id === repo.teamId)?.role ?? "viewer";
    } catch (e) {
      if (stale()) return;
      this.error = humanError(e);
      this.loading = false;
      return;
    }
    this.loading = false;
    if (this.months === null) return;
    void this.loadSource();
    if (this.selected) void this.refreshMonth();
  }

  private async refreshMonths() {
    try {
      const area = await getDataArea(this.repositoryId);
      if (area) this.months = sortMonths(area.months);
    } catch (e) {
      toast(humanError(e), "error");
    }
  }

  // ---- Month detail ----

  private selectMonth(month: string) {
    this.selected = month;
    this.detail = null;
    this.detailError = "";
    void this.refreshMonth();
    void this.updateComplete.then(() =>
      this.renderRoot
        .querySelector("#month")
        ?.scrollIntoView({ behavior: "smooth", block: "start" }),
    );
  }

  private openMonth() {
    const m = this.monthInput.trim();
    if (!monthAllowed(m)) {
      this.monthError = "Use YYYY-MM, from 1970-01 to next month.";
      return;
    }
    this.monthError = "";
    this.selectMonth(m);
  }

  private async refreshMonth() {
    const month = this.selected;
    if (!month) return;
    this.detailLoading = true;
    try {
      const d = await getMonth(this.repositoryId, month);
      if (month !== this.selected) return;
      this.detail = d;
      this.detailError = "";
    } catch (e) {
      if (month !== this.selected) return;
      this.detailError = humanError(e);
    } finally {
      this.detailLoading = false;
    }
  }

  private async confirmFileDelete() {
    const path = this.pendingFileDelete;
    this.pendingFileDelete = "";
    try {
      await deleteDataFile(this.repositoryId, this.selected, path);
      toast("File deleted", "success");
    } catch (e) {
      toast(humanError(e), "error");
    }
    void this.refreshMonth();
    void this.refreshMonths();
  }

  private async confirmUploadDelete() {
    const up = this.pendingUploadDelete;
    this.pendingUploadDelete = null;
    if (!up) return;
    try {
      await deleteUpload(this.repositoryId, this.selected, up.id);
      toast("Upload deleted", "success");
    } catch (e) {
      toast(humanError(e), "error");
    }
    void this.refreshMonth();
  }

  // ---- Uploads ----

  private pick(which: "files" | "folder" | "resume") {
    this.renderRoot.querySelector<HTMLInputElement>(`input[data-pick="${which}"]`)?.click();
  }

  private onPick(e: Event, folder: boolean) {
    const input = e.target as HTMLInputElement;
    const files = Array.from(input.files ?? []);
    input.value = "";
    const picked = files.map((file) => ({ file, path: folder ? folderPath(file) : file.name }));
    const kept = picked.filter((p) => !hidden(p.path));
    if (kept.length < picked.length) {
      const n = picked.length - kept.length;
      toast(`Skipped ${plural(n, "hidden file", "hidden files")}.`, "info");
    }
    this.enqueue(kept);
  }

  private resume(up: Upload) {
    this.resumeTarget = up;
    this.pick("resume");
  }

  private onPickResume(e: Event) {
    const input = e.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = "";
    const up = this.resumeTarget;
    this.resumeTarget = null;
    if (!file || !up) return;
    if (file.size !== up.size) {
      toast(
        `That file is ${formatBytes(file.size)}, but the upload expects ${formatBytes(up.size)}. ` +
          "Pick the same file.",
        "error",
      );
      return;
    }
    this.enqueue([{ file, path: up.path }]);
  }

  private enqueue(files: { file: File; path: string }[]) {
    if (files.length === 0) return;
    const added = files.map(
      (f): Transfer => ({
        key: this.nextKey++,
        repositoryId: this.repositoryId,
        month: this.selected,
        path: f.path,
        file: f.file,
        sent: 0,
        state: "waiting",
        error: "",
      }),
    );
    this.transfers = [...this.transfers.filter((t) => t.state !== "done"), ...added];
    void this.send();
  }

  private patch(key: number, p: Partial<Transfer>) {
    this.transfers = this.transfers.map((t) => (t.key === key ? { ...t, ...p } : t));
  }

  /**
   * send works through the waiting transfers one at a time: the server
   * holds a small number of pending uploads per repository, and one stream at
   * full speed ends each file sooner than several sharing the line.
   */
  private async send() {
    if (this.sending) return;
    this.sending = true;
    const signal = this.abort.signal;
    try {
      for (;;) {
        const t = this.transfers.find((x) => x.state === "waiting");
        if (!t || signal.aborted) break;
        this.patch(t.key, { state: "sending" });
        try {
          await uploadFile(
            t.repositoryId,
            t.month,
            t.path,
            t.file,
            (sent) => this.patch(t.key, { sent }),
            signal,
          );
          this.patch(t.key, { state: "done" });
        } catch (e) {
          if (signal.aborted) {
            // Left the page: sent again from the server's offset if it comes back.
            this.patch(t.key, { state: "waiting" });
            break;
          }
          this.patch(t.key, { state: "failed", error: detailedError(e) });
        }
        if (t.month === this.selected) void this.refreshMonth();
      }
    } finally {
      this.sending = false;
    }
    if (signal.aborted) {
      // A fresh controller means the page came back while this loop wound down.
      if (!this.abort.signal.aborted) void this.send();
      return;
    }
    void this.refreshMonths();
  }

  /** active reports whether this browser is sending the given path of the selected month. */
  private active(path: string): boolean {
    return this.transfers.some(
      (t) =>
        t.month === this.selected &&
        t.path === path &&
        (t.state === "waiting" || t.state === "sending"),
    );
  }

  // ---- Seal ----

  private async seal() {
    const month = this.selected;
    this.sealBusy = true;
    try {
      const { sealId } = await sealMonth(this.repositoryId, month);
      this.sealConfirm = false;
      this.sealWatch = { month, sealId };
      toast(`Sealing ${month}…`, "info");
      this.startWatch();
      void this.refreshMonth();
    } catch (e) {
      this.sealConfirm = false;
      toast(humanError(e), "error");
      void this.refreshMonth();
    } finally {
      this.sealBusy = false;
    }
  }

  private async checkSeal() {
    const w = this.sealWatch;
    if (!w) return;
    const d = await getMonth(this.repositoryId, w.month);
    if (w !== this.sealWatch) return;
    if (w.month === this.selected) this.detail = d;
    if (d.seals.some((s) => s.id === w.sealId)) {
      this.sealWatch = null;
      toast(`${w.month} sealed`, "success");
      void this.refreshMonths();
      return;
    }
    // The drop is open again, or gone, without a seal: the seal failed.
    if (d.open?.dropId !== w.sealId || d.open.status !== "sealing") {
      this.sealWatch = null;
      toast(`The seal of ${w.month} did not finish. Check the files and try again.`, "error");
      void this.refreshMonths();
    }
  }

  // ---- S3 source ----

  private async loadSource() {
    try {
      this.source = await getDataSource(this.repositoryId);
      this.sourceError = "";
      if (this.source?.lastPull?.status === "running") this.startWatch();
    } catch (e) {
      this.sourceError = humanError(e);
    }
  }

  private editSource() {
    const s = this.source;
    this.srcEndpoint = s?.endpoint ?? "";
    this.srcRegion = s?.region ?? "";
    this.srcBucket = s?.bucket ?? "";
    this.srcPrefix = s?.prefix ?? "";
    this.srcFormError = "";
    this.showSourceForm = true;
  }

  private async saveSource() {
    const endpoint = this.srcEndpoint.trim();
    const bucket = this.srcBucket.trim();
    if (!endpoint.startsWith("https://")) {
      this.srcFormError = "Enter the endpoint as an https:// URL.";
      return;
    }
    if (!bucket) {
      this.srcFormError = "Enter the bucket name.";
      return;
    }
    this.srcSaving = true;
    this.srcFormError = "";
    try {
      this.source = await setDataSource(this.repositoryId, {
        endpoint,
        region: this.srcRegion.trim(),
        bucket,
        prefix: this.srcPrefix.trim(),
      });
      this.showSourceForm = false;
      toast("S3 source saved", "success");
    } catch (e) {
      this.srcFormError = detailedError(e);
    } finally {
      this.srcSaving = false;
    }
  }

  private async removeSource() {
    this.sourceDeleteConfirm = false;
    try {
      await deleteDataSource(this.repositoryId);
      this.source = null;
      toast("S3 source removed", "success");
    } catch (e) {
      toast(humanError(e), "error");
    }
  }

  private async pull() {
    const month = this.selected;
    this.pullBusy = true;
    try {
      await pullMonth(this.repositoryId, month);
      toast(`Pulling ${month}…`, "info");
      // loadSource watches a pull that still runs; one that already ended
      // has changed the month now.
      await this.loadSource();
      if (this.source?.lastPull?.status !== "running") {
        void this.refreshMonth();
        void this.refreshMonths();
      }
    } catch (e) {
      toast(humanError(e), "error");
    } finally {
      this.pullBusy = false;
    }
  }

  private async checkPull() {
    if (this.source?.lastPull?.status !== "running") return;
    const s = await getDataSource(this.repositoryId);
    this.source = s;
    const last = s?.lastPull;
    if (!last || last.status === "running") return;
    if (last.status === "succeeded") toast(`Pulled ${last.month}`, "success");
    else toast(`The pull of ${last.month} failed${last.error ? `: ${last.error}` : "."}`, "error");
    if (last.month === this.selected) void this.refreshMonth();
    void this.refreshMonths();
  }

  // ---- Watch (seal and pull) ----

  private startWatch() {
    if (this.watchTimer === undefined) {
      this.watchTimer = window.setInterval(() => void this.tick(), POLL_MS);
    }
  }

  private stopWatch() {
    if (this.watchTimer !== undefined) {
      window.clearInterval(this.watchTimer);
      this.watchTimer = undefined;
    }
  }

  private async tick() {
    if (this.ticking) return;
    this.ticking = true;
    try {
      await Promise.all([this.checkSeal(), this.checkPull()]);
    } catch {
      /* transient read failure — the next tick tries again */
    } finally {
      this.ticking = false;
    }
    if (!this.sealWatch && this.source?.lastPull?.status !== "running") this.stopWatch();
  }

  // ---- Render ----

  override render() {
    if (this.loading && !this.repo) {
      return html`<bn-spinner block label="Loading data…"></bn-spinner>`;
    }
    const backHref = `/repositories/${encodeURIComponent(this.repositoryId)}`;
    const back = () => navigate(backHref);
    if (this.error || !this.repo) {
      return html`<bn-empty-state
        icon="⚠"
        heading="Couldn’t load the data area"
        message=${this.error || "Repository not found."}
      >
        <bn-button slot="actions" variant="secondary" @click=${() => navigate("/workspaces")}
          >Back to workspaces</bn-button
        >
      </bn-empty-state>`;
    }
    if (this.months === null) {
      return html`<bn-empty-state
        icon="∅"
        heading="No data area"
        message="The data area is not enabled on this server."
      >
        <bn-button slot="actions" variant="secondary" @click=${back}>Back to repository</bn-button>
      </bn-empty-state>`;
    }
    return html`
      <a
        class="back"
        href=${backHref}
        @click=${(e: Event) => {
          e.preventDefault();
          back();
        }}
        >← ${this.repo.name}</a
      >
      <div class="head">
        <h1>Data</h1>
        <span class="spacer"></span>
        <bn-button variant="ghost" size="sm" @click=${() => this.load()}>Refresh</bn-button>
      </div>

      <div class="sections">
        ${this.renderMonths(this.months)} ${this.selected ? this.renderMonth() : nothing}
        ${this.renderTransfers()} ${this.renderSource()}
      </div>

      <input
        type="file"
        multiple
        data-pick="files"
        style="display:none"
        @change=${(e: Event) => this.onPick(e, false)}
      />
      <input
        type="file"
        multiple
        webkitdirectory
        data-pick="folder"
        style="display:none"
        @change=${(e: Event) => this.onPick(e, true)}
      />
      <input
        type="file"
        data-pick="resume"
        style="display:none"
        @change=${(e: Event) => this.onPickResume(e)}
      />
      ${this.renderModals()}
    `;
  }

  private renderMonths(months: MonthSummary[]) {
    const canWrite = roleAtLeast(this.teamRole, "member");
    return html`<bn-card heading="Months" flush>
      <div class="pad">
        <p class="hint">
          Upload a month's files into its open data drop, then seal it. A seal is read-only for
          good. Builds of this repository's workspaces read the newest seal of every month at
          <span class="mono">/data/YYYY-MM/</span>.
        </p>
        ${canWrite
          ? html`<div class="row" style="margin-top:var(--bino-space-md)">
              <bn-field label="Month" hint="YYYY-MM, up to next month." error=${this.monthError}>
                <bn-input
                  mono
                  .value=${this.monthInput}
                  placeholder="2026-01"
                  ?invalid=${this.monthError !== ""}
                  @input=${(e: CustomEvent<{ value: string }>) =>
                    (this.monthInput = e.detail.value)}
                  @keydown=${(e: KeyboardEvent) => {
                    if (e.key === "Enter") this.openMonth();
                  }}
                ></bn-input>
              </bn-field>
              <bn-field label=" ">
                <bn-button variant="secondary" @click=${() => this.openMonth()}
                  >Open month</bn-button
                >
              </bn-field>
            </div>`
          : nothing}
      </div>
      <bn-table
        .columns=${[
          { key: "month", label: "Month", mono: true },
          { key: "open", label: "Open drop" },
          { key: "latest", label: "Newest seal" },
          { key: "seals", label: "Seals", align: "right", width: "1px" },
        ] as BnColumn[]}
        .rows=${months.map(
          (m): BnRow => ({
            month: html`<button class="link-btn" @click=${() => this.selectMonth(m.month)}>
              ${m.month}
            </button>`,
            open: m.open
              ? html`<bn-badge variant=${m.open.status === "sealing" ? "warning" : "info"}
                    ?dot=${m.open.status === "sealing"}
                    >${m.open.status}</bn-badge
                  >
                  ${plural(m.open.files, "file", "files")}, ${formatBytes(m.open.bytes)}`
              : "—",
            latest: m.latestSeal
              ? html`<span class="mono" title=${m.latestSeal.digest}
                    >${m.latestSeal.digest.slice(0, 12)}</span
                  >
                  · ${plural(m.latestSeal.files, "file", "files")},
                  ${formatBytes(m.latestSeal.bytes)} · ${formatTime(m.latestSeal.sealedAt)}`
              : "—",
            seals: String(m.seals),
          }),
        )}
        empty="No data yet. Open a month and upload its files."
      ></bn-table>
    </bn-card>`;
  }

  private renderMonth() {
    const d = this.detail;
    const canWrite = roleAtLeast(this.teamRole, "member");
    const open = d?.open ?? null;
    const sealing = open?.status === "sealing";
    const busy = this.transfers.some(
      (t) => t.month === this.selected && (t.state === "waiting" || t.state === "sending"),
    );
    const sealable =
      d !== null &&
      open !== null &&
      !sealing &&
      open.files.length > 0 &&
      d.uploads.length === 0 &&
      !busy;
    return html`<bn-card id="month" heading=${`Month ${this.selected}`} flush>
      <div slot="actions" class="actions-row">
        <bn-button size="sm" variant="ghost" ?loading=${this.detailLoading} @click=${() =>
          this.refreshMonth()}>Refresh</bn-button>
        ${canWrite
          ? html`<bn-button size="sm" variant="secondary" ?disabled=${sealing} @click=${() =>
                this.pick("files")}>Upload files</bn-button>
              <bn-button size="sm" variant="secondary" ?disabled=${sealing} @click=${() =>
                this.pick("folder")}>Upload folder</bn-button>
              ${this.source
                ? html`<bn-button
                    size="sm"
                    variant="secondary"
                    ?disabled=${sealing || this.source.lastPull?.status === "running"}
                    ?loading=${this.pullBusy}
                    @click=${() => this.pull()}
                    >Pull month</bn-button
                  >`
                : nothing}
              <bn-button size="sm" variant="primary" ?disabled=${!sealable} @click=${() =>
                (this.sealConfirm = true)}>Seal month</bn-button>`
          : nothing}
      </div>
      ${this.detailError
        ? html`<div class="pad"><p class="err">${this.detailError}</p></div>`
        : !d
          ? html`<bn-spinner block label="Loading month…"></bn-spinner>`
          : html`${this.renderOpen(d, canWrite)} ${this.renderUploads(d, canWrite)}
            ${this.renderSeals(d)}`}
    </bn-card>`;
  }

  private renderOpen(d: MonthDetail, canWrite: boolean) {
    const open = d.open;
    if (!open) {
      return html`<h3>Open data drop</h3>
        <div class="pad">
          <p class="hint">No open data drop. Uploaded files start one.</p>
        </div>`;
    }
    const sealing = open.status === "sealing";
    return html`<h3>
        Open data drop
        <bn-badge variant=${sealing ? "warning" : "info"} ?dot=${sealing}>${open.status}</bn-badge>
      </h3>
      <div class="pad" style="padding-top:0">
        <p class="hint">
          ${plural(open.files.length, "file", "files")}, ${formatBytes(open.bytes)}.
          ${sealing ? "Sealing. The files cannot change now." : ""}
        </p>
      </div>
      <bn-table
        .columns=${[
          { key: "path", label: "Path", mono: true },
          { key: "size", label: "Size", align: "right", mono: true, width: "1px" },
          { key: "actions", label: "", align: "right", width: "1px" },
        ] as BnColumn[]}
        .rows=${open.files.map(
          (f): BnRow => ({
            path: f.path,
            size: formatBytes(f.size),
            actions:
              canWrite && !sealing
                ? html`<bn-button
                    size="sm"
                    variant="danger"
                    @click=${() => (this.pendingFileDelete = f.path)}
                    >Delete</bn-button
                  >`
                : nothing,
          }),
        )}
        empty="No files yet."
      ></bn-table>`;
  }

  private renderUploads(d: MonthDetail, canWrite: boolean) {
    if (d.uploads.length === 0) return nothing;
    return html`<h3>Unfinished uploads</h3>
      <div class="pad" style="padding-top:0">
        <p class="hint">
          To continue an upload, pick the same file again. Uploads idle for a day are removed.
        </p>
      </div>
      <bn-table
        .columns=${[
          { key: "path", label: "Path", mono: true },
          { key: "progress", label: "Received" },
          { key: "actions", label: "", align: "right", width: "1px" },
        ] as BnColumn[]}
        .rows=${d.uploads.map(
          (u): BnRow => ({
            path: u.path,
            progress: html`<div class="progress">
              <progress max="100" .value=${percent(u.offset, u.size)}></progress>
              <span class="mono">${formatBytes(u.offset)} / ${formatBytes(u.size)}</span>
            </div>`,
            actions:
              canWrite && !this.active(u.path)
                ? html`<div class="actions-row" style="justify-content:flex-end">
                    <bn-button size="sm" variant="secondary" @click=${() => this.resume(u)}
                      >Resume</bn-button
                    >
                    <bn-button
                      size="sm"
                      variant="danger"
                      @click=${() => (this.pendingUploadDelete = u)}
                      >Delete</bn-button
                    >
                  </div>`
                : nothing,
          }),
        )}
      ></bn-table>`;
  }

  private renderSeals(d: MonthDetail) {
    const seals = [...d.seals].sort((a, b) => (a.sealedAt < b.sealedAt ? 1 : -1));
    return html`<h3>Seals</h3>
      <bn-table
        .columns=${[
          { key: "digest", label: "Digest" },
          { key: "files", label: "Files", align: "right", mono: true, width: "1px" },
          { key: "size", label: "Size", align: "right", mono: true, width: "1px" },
          { key: "by", label: "Sealed by" },
          { key: "at", label: "Sealed" },
        ] as BnColumn[]}
        .rows=${seals.map(
          (s, i): BnRow => ({
            digest: html`<div class="actions-row">
              <span class="mono" title=${s.digest}>${s.digest.slice(0, 12)}</span>
              <bn-button size="sm" variant="ghost" @click=${() => void this.copy(s.digest)}
                >Copy</bn-button
              >
              ${i === 0 ? html`<bn-badge variant="success">in use</bn-badge>` : nothing}
            </div>`,
            files: String(s.files),
            size: formatBytes(s.bytes),
            by: s.sealedBy || (s.sealedByKey ? `API key ${s.sealedByKey}` : "—"),
            at: formatTime(s.sealedAt),
          }),
        )}
        empty="This month has no seal yet."
      ></bn-table>`;
  }

  private renderTransfers() {
    if (this.transfers.length === 0) return nothing;
    const finished = this.transfers.some((t) => t.state === "done" || t.state === "failed");
    return html`<bn-card heading="Uploads from this browser" flush>
      ${finished
        ? html`<bn-button
            slot="actions"
            size="sm"
            variant="ghost"
            @click=${() =>
              (this.transfers = this.transfers.filter(
                (t) => t.state === "waiting" || t.state === "sending",
              ))}
            >Clear finished</bn-button
          >`
        : nothing}
      <bn-table
        .columns=${[
          { key: "month", label: "Month", mono: true, width: "1px" },
          { key: "path", label: "Path", mono: true },
          { key: "progress", label: "Progress" },
          { key: "state", label: "Status" },
        ] as BnColumn[]}
        .rows=${this.transfers.map(
          (t): BnRow => ({
            month: t.month,
            path: t.path,
            progress: html`<div class="progress">
              <progress max="100" .value=${percent(t.sent, t.file.size)}></progress>
              <span class="mono">${percent(t.sent, t.file.size)}%</span>
            </div>`,
            state:
              t.state === "failed"
                ? html`<bn-badge variant="error">failed</bn-badge>
                    <span class="err">${t.error}</span>`
                : html`<bn-badge
                    variant=${t.state === "done"
                      ? "success"
                      : t.state === "sending"
                        ? "info"
                        : "neutral"}
                    ?dot=${t.state === "sending"}
                    >${t.state}</bn-badge
                  >`,
          }),
        )}
      ></bn-table>
    </bn-card>`;
  }

  private renderSource() {
    const canManage = roleAtLeast(this.teamRole, "maintainer");
    const s = this.source;
    return html`<bn-card heading="S3 source">
      ${canManage
        ? html`<div slot="actions" class="actions-row">
            ${s
              ? html`<bn-button size="sm" variant="danger" @click=${() =>
                  (this.sourceDeleteConfirm = true)}>Remove</bn-button>`
              : nothing}
            <bn-button size="sm" variant="secondary" @click=${() => this.editSource()}
              >${s ? "Edit" : "Add S3 source"}</bn-button
            >
          </div>`
        : nothing}
      ${this.sourceError
        ? html`<p class="err">${this.sourceError}</p>`
        : !s
          ? html`<p class="hint">
              No S3 source. With one, a member can pull a month from a bucket into its open data
              drop instead of uploading it.
            </p>`
          : html`<div class="meta">
                <div><div class="k">Endpoint</div><div class="v">${s.endpoint}</div></div>
                <div><div class="k">Region</div><div class="v">${s.region || "—"}</div></div>
                <div><div class="k">Bucket</div><div class="v">${s.bucket}</div></div>
                <div><div class="k">Prefix</div><div class="v">${s.prefix || "—"}</div></div>
                <div>
                  <div class="k">Last pull</div>
                  <div class="v">
                    ${s.lastPull
                      ? html`<bn-badge
                            .variant=${pullVariant(s.lastPull.status)}
                            ?dot=${s.lastPull.status === "running"}
                            >${s.lastPull.status}</bn-badge
                          >
                          ${s.lastPull.month} · ${formatTime(s.lastPull.at)}`
                      : "—"}
                  </div>
                </div>
              </div>
              ${s.lastPull?.status === "failed" && s.lastPull.error
                ? html`<p class="err" style="margin-top:var(--bino-space-md)">
                    ${s.lastPull.error}
                  </p>`
                : nothing}
              ${s.credentials
                ? nothing
                : html`<div class="warn">
                    The keys are missing. Add the repository secrets
                    <span class="mono">BINO_S3_ACCESS_KEY_ID</span> and
                    <span class="mono">BINO_S3_SECRET_ACCESS_KEY</span> on the repository page.
                  </div>`}`}
    </bn-card>`;
  }

  private renderModals() {
    const open = this.detail?.open;
    return html`
      <bn-modal
        ?open=${this.sealConfirm}
        heading=${`Seal ${this.selected}?`}
        @close=${() => (this.sealConfirm = false)}
      >
        <p>
          The ${plural(open?.files.length ?? 0, "file", "files")}
          (${formatBytes(open?.bytes ?? 0)}) of the open data drop for
          <strong>${this.selected}</strong> become read-only for good. New builds read this seal
          for ${this.selected}. Files uploaded later start a new, empty drop. Its seal replaces
          this one for new builds, so it must hold every file of the month.
        </p>
        <bn-button slot="footer" variant="ghost" @click=${() => (this.sealConfirm = false)}
          >Cancel</bn-button
        >
        <bn-button slot="footer" variant="primary" ?loading=${this.sealBusy} @click=${() =>
          this.seal()}>Seal month</bn-button>
      </bn-modal>

      <bn-modal
        ?open=${this.pendingFileDelete !== ""}
        heading="Delete file?"
        @close=${() => (this.pendingFileDelete = "")}
      >
        <p>
          Delete <strong class="mono">${this.pendingFileDelete}</strong> from the open data drop
          for ${this.selected}? Seals are not affected.
        </p>
        <bn-button slot="footer" variant="ghost" @click=${() => (this.pendingFileDelete = "")}
          >Cancel</bn-button
        >
        <bn-button slot="footer" variant="danger" @click=${() => this.confirmFileDelete()}
          >Delete</bn-button
        >
      </bn-modal>

      <bn-modal
        ?open=${this.pendingUploadDelete !== null}
        heading="Delete upload?"
        @close=${() => (this.pendingUploadDelete = null)}
      >
        <p>
          Delete the unfinished upload of
          <strong class="mono">${this.pendingUploadDelete?.path}</strong>? The bytes it received
          are removed.
        </p>
        <bn-button slot="footer" variant="ghost" @click=${() => (this.pendingUploadDelete = null)}
          >Cancel</bn-button
        >
        <bn-button slot="footer" variant="danger" @click=${() => this.confirmUploadDelete()}
          >Delete</bn-button
        >
      </bn-modal>

      <bn-modal
        ?open=${this.showSourceForm}
        heading=${this.source ? "Edit S3 source" : "Add S3 source"}
        @close=${() => (this.showSourceForm = false)}
      >
        <bn-field
          label="Endpoint"
          required
          hint="e.g. https://s3.eu-central-1.amazonaws.com"
          error=${this.srcFormError}
        >
          <bn-input
            mono
            .value=${this.srcEndpoint}
            placeholder="https://"
            @input=${(e: CustomEvent<{ value: string }>) => (this.srcEndpoint = e.detail.value)}
          ></bn-input>
        </bn-field>
        <bn-field label="Region">
          <bn-input
            mono
            .value=${this.srcRegion}
            @input=${(e: CustomEvent<{ value: string }>) => (this.srcRegion = e.detail.value)}
          ></bn-input>
        </bn-field>
        <bn-field label="Bucket" required>
          <bn-input
            mono
            .value=${this.srcBucket}
            @input=${(e: CustomEvent<{ value: string }>) => (this.srcBucket = e.detail.value)}
          ></bn-input>
        </bn-field>
        <bn-field
          label="Prefix"
          required
          hint="The key prefix of a month inside the bucket, with {month} once, e.g. exports/{month}/."
        >
          <bn-input
            mono
            placeholder="{month}/"
            .value=${this.srcPrefix}
            @input=${(e: CustomEvent<{ value: string }>) => (this.srcPrefix = e.detail.value)}
          ></bn-input>
        </bn-field>
        <p class="hint">
          The keys are the repository secrets <span class="mono">BINO_S3_ACCESS_KEY_ID</span> and
          <span class="mono">BINO_S3_SECRET_ACCESS_KEY</span>.
        </p>
        <bn-button slot="footer" variant="ghost" @click=${() => (this.showSourceForm = false)}
          >Cancel</bn-button
        >
        <bn-button slot="footer" variant="primary" ?loading=${this.srcSaving} @click=${() =>
          this.saveSource()}>Save</bn-button>
      </bn-modal>

      <bn-modal
        ?open=${this.sourceDeleteConfirm}
        heading="Remove S3 source?"
        @close=${() => (this.sourceDeleteConfirm = false)}
      >
        <p>Remove the S3 source? Data already pulled stays. The repository secrets stay too.</p>
        <bn-button slot="footer" variant="ghost" @click=${() => (this.sourceDeleteConfirm = false)}
          >Cancel</bn-button
        >
        <bn-button slot="footer" variant="danger" @click=${() => this.removeSource()}
          >Remove</bn-button
        >
      </bn-modal>
    `;
  }

  private async copy(text: string) {
    try {
      await navigator.clipboard.writeText(text);
      toast("Digest copied", "success");
    } catch {
      toast("Couldn't copy. Select the text and copy it yourself.", "error");
    }
  }
}

/** sortMonths puts the newest month first. */
function sortMonths(months: MonthSummary[]): MonthSummary[] {
  return [...months].sort((a, b) => (a.month < b.month ? 1 : -1));
}

declare global {
  interface HTMLElementTagNameMap {
    "page-data": PageData;
  }
}
